<template>
    <div class="pdd-container">
        <div class="pdd-title"
             :class="{'pdd-title-selected': individualEntrySelected}"
             @click="onTitleBarClick">
            <slot name="before-title"></slot>

            <template v-if="title.includes(':')">
                <span class="light">{{ title.split(':')[0].toUpperCase() }}</span>
                <span class="bold">{{ title.split(':')[1].toUpperCase() }}</span>
            </template>
            <span v-else class="bold">{{ title }}</span>

            <div v-if="subtitle" class="pdd-subtitle">{{ subtitle }}</div>

            <template v-if="displayPdChart">
                <button class="info-video-button" :title="$t('roster.comp.personnelUsageInfoVideo')" @click.stop="openInfoVideoPopup()">
                    <i class="fas fa-info-square"></i>
                </button>
            </template>
        </div>

        <div class="pdd-sidebar-container">
            <md-tab-group class="pdd-tab-group"
                          :class="{'pdd-tab-group--indent': ['feedback', 'productivity'].includes(pdSelectedStat)}"
                          size="sm"
                          :selected="selectedTab" @select="onTabSelect" ref="tabGroup">
                <!--                <md-tab title="Statistiken" icon="fas fa-chart-line">-->
                <md-tab title="">

                    <div class="pdd-header-row" v-if="showPrevMonthComp">
                        <div>{{ $t('roster.comp.statistic') }}</div>
                        <div>{{ $t('roster.comp.emergent') }}</div>
                        <div>{{ $t('roster.comp.current') }}</div>
                        <div>{{ $t('roster.comp.trend') }}</div>
                    </div>

                    <div class="pdd-body" v-if="stats">

                        <template v-if="pdSelectedStat === 'productivity'">

                            <div class="section-header">
                                {{ $t('roster.view.productivity') }}
                                {{ selectedIndicatorPostfix.length > 0 ? '(' + selectedIndicatorPostfix + ')' : '' }}

                                <md-hover-button class="info-button" icon="fa-info">
                                    <div class="hover-text-container">
                                        {{ getRowHelperText('productivityText') }}
                                    </div>
                                </md-hover-button>
                            </div>

                            <div class="pdd-body-row">
                                <detail-row v-for="row of detailRows(2)"
                                            :key="row.key"
                                            :stat-key="row.key"
                                            :stat-value="row.value"
                                            :show-past-comp="showPrevMonthComp"
                                            :past-stat-value="row.pastValue"
                                            :stat-title="row.title"
                                            :is-percentage="row.isPercentage"
                                            :postfix="row.postfix"
                                            :helper-text="row.helperText"
                                            :expanded="row.expanded"
                                            :indented="row.indented"
                                            :selected="false"
                                            :selectable="false"
                                            :round-to="row.roundTo"
                                            @expand="onRowExpand"/>
                            </div>

                        </template>

                        <template v-else-if="pdSelectedStat === 'feedback'">

                            <div class="section-header">
                                {{ $t('roster.comp.satisfaction') }}

                                <md-hover-button class="info-button" icon="fa-info">
                                    <div class="hover-text-container">
                                        {{ getRowHelperText('feedbackText') }}
                                    </div>
                                </md-hover-button>
                            </div>

                            <div class="pdd-body-row">
                                <detail-row v-for="row of detailRows(3)"
                                            :key="row.key"
                                            :stat-key="row.key"
                                            :stat-value="row.value"
                                            :show-past-comp="showPrevMonthComp"
                                            :past-stat-value="row.pastValue"
                                            :stat-title="row.title"
                                            :is-percentage="row.isPercentage"
                                            :postfix="row.postfix"
                                            :helper-text="row.helperText"
                                            :expanded="row.expanded"
                                            :indented="row.indented"
                                            :selected="false"
                                            :selectable="false"
                                            :round-to="row.roundTo"
                                            @expand="onRowExpand"/>
                            </div>

                        </template>

                        <template v-else>

                            <div class="section-header">
                                {{ $t('roster.comp.optimization') + ' ' + stats.indicatorTypeName ? `(${stats.indicatorTypeName})` : '' }}
                                <button class="stats-button" @click="openSumPopup" v-if="!individualEntrySelected">
                                    {{ $t('roster.comp.sums') }} <i class="far fa-external-link-square-alt"/>
                                </button>
                            </div>

                            <detail-row v-for="row of detailRows(0)"
                                        :key="row.key"
                                        :stat-key="row.key"
                                        :stat-value="row.value"
                                        :show-past-comp="showPrevMonthComp"
                                        :past-stat-value="row.pastValue"
                                        :stat-title="row.title"
                                        :is-percentage="row.isPercentage"
                                        :postfix="row.postfix"
                                        :helper-text="row.helperText"
                                        :expanded="row.expanded"
                                        :indented="row.indented"
                                        :selected="row.selected"
                                        :selectable="!['productivity', 'feedback'].includes(row.key)"
                                        :round-to="row.roundTo"
                                        @statSelect="onStatSelect"
                                        @expand="onRowExpand"/>

                            <span class="section-header">
                                {{ $t('roster.comp.forecastDeviation') }}
                            </span>

                            <detail-row v-for="row of detailRows(1)"
                                        :key="row.key"
                                        :stat-key="row.key"
                                        :stat-value="row.value"
                                        :show-past-comp="showPrevMonthComp"
                                        :past-stat-value="row.pastValue"
                                        :stat-title="row.title"
                                        :is-percentage="row.isPercentage"
                                        :postfix="row.postfix"
                                        :helper-text="row.helperText"
                                        :helper-text-alignment="'top'"
                                        :expanded="row.expanded"
                                        :indented="row.indented"
                                        :selected="row.selected"
                                        :selectable="!['productivity', 'feedback'].includes(row.key)"
                                        :round-to="row.roundTo"
                                        @statSelect="onStatSelect"
                                        @expand="onRowExpand"/>

                            <detail-row v-if="stats.predictionUntil"
                                        :stat-title="$t('roster.comp.forecastFrom') + ':'"
                                        :stat-value="stats.predictionUntil.format('DD.MM.')"
                                        :selectable="false"/>

                        </template>
                    </div>

                </md-tab>

                <!--                <md-tab title="Werte" icon="fas fa-clock">

                                    <div class="pdd-body" v-if="stats">

                                        <detail-row v-for="row of detailRows(1)"
                                                    :key="row.key"
                                                    :stat-key="row.key"
                                                    :stat-value="row.value"
                                                    :show-past-comp="false"
                                                    :past-stat-value="row.pastValue"
                                                    :stat-title="row.title"
                                                    :is-percentage="row.isPercentage"
                                                    :postfix="row.postfix"
                                                    :helper-text="row.helperText"
                                                    :expanded="row.expanded"
                                                    :indented="row.indented"
                                                    :selected="row.selected"
                                                    :round-to="row.roundTo"
                                                    @statSelect="onStatSelect"
                                                    @expand="onRowExpand"/>

                                        &lt;!&ndash;
                                        <template v-if="stats.mode === 'month'">

                                          <div class="pdd-row">
                                            <div>{{ stats.indicatorName }}</div>
                                            <div class="bold" v-if="!isNaN(stats.totalIndicatorSum)">
                                              {{ stats.totalIndicatorSum.toFixed(2) }}
                                            </div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                          <hr>

                                          <div class="pdd-row">
                                            <div>Produktivität</div>
                                            <div class="bold" v-if="!isNaN(stats.productivity)">
                                              {{ stats.productivity.toFixed(2) }}
                                            </div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                          <div class="pdd-row">
                                            <div>Sollstunden</div>
                                            <div class="bold" v-if="!isNaN(stats.plannedHours)">{{
                                                stats.plannedHours.toFixed(2)
                                              }}
                                            </div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                          <div class="pdd-row">
                                            <div>Übertrag aus dem Vormonat</div>
                                            <div class="bold" v-if="!isNaN(stats.prevMonthBalance)">
                                              {{ stats.prevMonthBalance.toFixed(2) }}
                                            </div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>
                                          <div class="pdd-row" v-if="!isNaN(stats.preferenceHours)">
                                            <div>Geplante Abwesenheiten</div>
                                            <div class="bold">{{ stats.preferenceHours.toFixed(2) }}</div>
                                          </div>

                                        </template>

                                        <template v-else>

                                          <div class="pdd-row">
                                            <div>Geplant</div>
                                            <div v-if="!isNaN(stats.plannedHours)">{{ stats.plannedHours.toFixed(2) }}</div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                          <div class="pdd-row">
                                            <div>Eingeteilt</div>
                                            <div v-if="!isNaN(stats.assignedHours)">{{ stats.assignedHours.toFixed(2) }}</div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                          <div class="pdd-row">
                                            <div>Gestempelt</div>
                                            <div v-if="!isNaN(stats.actualHours)">{{ stats.actualHours.toFixed(2) }}</div>
                                            <div v-else>&#45;&#45;</div>
                                          </div>

                                        </template>&ndash;&gt;

                                    </div>

                                </md-tab>-->

                <!--                <md-tab :title="comparisonText"
                                        :icon="showPrevMonthComp ? 'fas fa-eye-slash' : 'fas fa-exchange'"/>-->

            </md-tab-group>

            <div class="pdd-sidebar" :style="{height: sidebarHeight}" v-if="pdSelectedStat === 'feedback'">
                <i class="pdd-sidebar-icon fas fa-laugh" style="color: var(--md);"></i>
                <i class="pdd-sidebar-icon fas fa-smile" style="color: var(--md);"></i>
                <i class="pdd-sidebar-icon fas fa-meh" style="color: var(--yellow);"></i>
                <i class="pdd-sidebar-icon fas fa-frown-open" style="color: var(--yellow);"></i>
                <i class="pdd-sidebar-icon fas fa-angry" style="color: var(--col-danger);"></i>
            </div>

            <div class="pdd-sidebar" :style="{height: sidebarHeight}" v-if="pdSelectedStat === 'productivity'">
                <i class="pdd-sidebar-icon fas fa-briefcase" style="font-size: 1.25rem; color: var(--md);"></i>
                <i class="pdd-sidebar-icon fas fa-briefcase" style="font-size: 1.2rem; color: var(--md);"></i>
                <i class="pdd-sidebar-icon fas fa-briefcase" style="font-size: 1.15rem; color: var(--yellow);"></i>
                <i class="pdd-sidebar-icon fas fa-briefcase" style="font-size: 1.1rem; color: var(--yellow);"></i>
                <i class="pdd-sidebar-icon fas fa-briefcase" style="font-size: 1.05rem; color: var(--col-danger);"></i>
            </div>
        </div>
    </div>
</template>

<script>
import DetailRow from '@/components/uicomponents/charts/DetailRow';
import { mapGetters } from 'vuex';
import { DISPLAY_FORMATTERS } from '@/components/uicomponents/charts/pdhelper';
import moment from 'moment';

export default {
    name: 'PersonnelDeploymentDetail',
    components: { DetailRow },
    props: {
        title: String,
    },
    data() {
        // const selectedTab = parseInt(localStorage.getItem('pdd-selected-tab')) || 0;
        return {
            selectedTab: 0,
            showPrevMonthComp: false,
            expandedRows: {},
            // infoVideoOpened: false,
        }
    },
    computed: {
        ...mapGetters([ 'pdDetailData', 'pdPastDetailData', 'pdSelectedStat', 'pdChartAreaHeight', 'displayPdChart' ]),

        stats() {
            return this.pdDetailData;
        },

        pastStats() {
            return this.pdPastDetailData;
        },

        individualEntrySelected() {
            return !!( this.stats || {} ).unitKey;
        },

        subtitle() {
            return this.individualEntrySelected ? this.stats.unitDisplay : null;
        },

        comparisonText() {
            const showSidebar = this.pdSelectedStat === 'feedback' || this.pdSelectedStat === 'productivity';
            if (showSidebar) {
                return this.$t('roster.comp.comparisonShort');
            } else {
                return this.showPrevMonthComp ? this.$t('roster.comp.comparisonFrom') : this.$t('roster.comp.comparison');
            }
        },

        sidebarHeight() {
            return ( this.pdChartAreaHeight + 15 - 12 ) + 'px';
        },

        showValuesInHours() {
            return this.pdDetailData.mode !== 'month' || this.pdDetailData.displayHours;
        },

        plannedValuesFrom() {
            if (this.pdDetailData.mode !== 'month') return null;

            const indicatorsPerDay = ( this.stats || { perUnit: {} } ).perUnit;

            let plannedValuesFrom = null;

            for (const [ dateKey, { actualIndicatorSum, plannedIndicatorSum } ] of Object.entries(indicatorsPerDay)) {
                const date = new Date(dateKey);
                if (plannedIndicatorSum && !actualIndicatorSum) {
                    plannedValuesFrom = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
                    break;
                }
            }

            return plannedValuesFrom;
        },

        indicatorPostfixMap() {
            return {
                1: '€',
                2: '€',
                3: '',
            };
        },

        selectedIndicatorPostfix() {
            if (!this.stats) return '';
            const indicatorTypeCategory = this.stats.indicatorTypeCategory;
            return this.indicatorPostfixMap[indicatorTypeCategory] || '';
        },
    },
    methods: {
        onTitleBarClick() {
            this.$emit('titleBarClick');
        },

        onTabSelect(tab) {
            if (tab === 2) {
                this.showPrevMonthComp = !this.showPrevMonthComp;
                this.$nextTick(() => this.$refs.tabGroup.tabs = this.$refs.tabGroup.findTabs());
            } else {
                this.selectedTab = tab;
                localStorage.setItem('pdd-selected-tab', tab);
            }
        },

        onStatSelect(stat) {
            // TODO: Improve this
            if (stat === 'feedback' || stat === 'productivity') return;
            if (this.selectedTab === 1) return;
            stat = this.pdSelectedStat === stat ? null : stat;
            this.$emit('statSelect', { stat });
            this.$store.commit('setPDSelectedStat', { pdSelectedStat: stat });
        },

        onRowExpand(row) {
            this.$set(this.expandedRows, row, !this.expandedRows[row]);
        },

        detailRows(tab) {
            const detailRows = [];

            for (const key of this.getVisibleRows(tab)) {
                detailRows.push({
                    key,
                    value: this.getRowValue(key),
                    pastValue: this.getRowValue(key, true),
                    title: this.getRowName(key),
                    isPercentage: this.rowIsPercentage(key),
                    helperText: this.getRowHelperText(key),
                    prefix: this.getRowPrefix(key),
                    postfix: this.getRowPostfix(key),
                    expanded: this.rowIsExpandable(key) ? !!this.expandedRows[key] : null,
                    indented: this.rowIsIndented(key),
                    selected: this.pdSelectedStat === key,
                    roundTo: this.rowRoundTo(key),
                });
            }

            return detailRows;
        },

        getVisibleRows(tab) {
            // TODO: Add available wt sum to tab 1
            let rowsForTab0 = [ 'accuracy', 'overstaffing', 'understaffing', 'utilization', 'coverage' ];
            if (this.stats.lineMode === 'planned') {
                rowsForTab0 = [ 'accuracyPlanned', 'overstaffingPlanned', 'understaffingPlanned', 'utilization', 'coverage' ];
            } else if (this.stats.lineMode === 'actual') {
                rowsForTab0 = [ 'accuracyActual', 'overstaffingActual', 'understaffingActual', 'utilization', 'coverage' ];
            }
            const rowsForTab = {
                // 0: [ [ 'accuracy', 'overstaffing', 'understaffing' ], 'utilization', 'coverage' ],
                0: rowsForTab0,
                1: [ 'prediction' ],
                2: [ [ 'productivityActual', 'productivityActualMin', 'productivityActualMax', ], 'productivityPlanned', 'productivityDeviation' ],
                3: [ 'feedbackUntil', 'feedbackCoverage', 'feedback' ],
                // 1: [ [ 'combinedIndicator', 'actualIndicator', 'plannedIndicator' ], 'actualWorkTime', 'plannedWorkTime' ],
            };

            // if (!this.individualEntrySelected) {
            //     rowsForTab[0].push('feedback');
            // }

            // if (tab === 1 && this.individualEntrySelected) {
            //     rowsForTab[1].splice(1, 0, [ 'combinedMeo', 'actualMeo', 'plannedMeo' ]);
            // }

            rowsForTab[tab] = rowsForTab[tab].flatMap((row) => {
                if (!Array.isArray(row)) return [ row ];
                if (this.expandedRows[row[0]]) return row;
                else return [ row[0] ];
            });

            // if (tab === 1 && this.stats && this.stats.mode === 'month' && !this.individualEntrySelected) {
            //     rowsForTab[1].push('availableWorkTime', 'prevMonthBalance', 'absenceHours');
            // }

            // if (tab === 1 && this.stats && this.individualEntrySelected) {
            //     rowsForTab[1].push('marketEmployeeCounts');
            // }

            return rowsForTab[tab] || [];
        },

        getRowName(row) {
            const rowNames = {
                accuracy: this.$t('roster.comp.accuracy'),
                accuracyActual: this.$t('roster.comp.accuracy') + this.$t('roster.comp.actualBubble'),
                accuracyPlanned: this.$t('roster.comp.accuracy') + this.$t('roster.comp.targetBubble'),
                overstaffing: this.$t('roster.comp.overstaffed'),
                overstaffingActual: this.$t('roster.comp.overstaffed') + this.$t('roster.comp.actualBubble'),
                overstaffingPlanned: this.$t('roster.comp.overstaffed') + this.$t('roster.comp.targetBubble'),
                understaffing: this.$t('roster.comp.understaffed'),
                understaffingActual: this.$t('roster.comp.understaffed') + this.$t('roster.comp.actualBubble'),
                understaffingPlanned: this.$t('roster.comp.understaffed') + this.$t('roster.comp.targetBubble'),
                prediction: this.$t('roster.comp.forecast'),
                utilization: this.$t('roster.comp.workload'),
                utilizationActual: this.$t('roster.comp.workload') + this.$t('roster.comp.actualBubble'),
                utilizationPlanned: this.$t('roster.comp.workload') + this.$t('roster.comp.targetBubble'),
                coverage: this.$t('roster.comp.coverage'),
                productivityActual: this.$tc('global.general.month') + this.$t('roster.comp.actualBubble'),
                productivityActualMin: this.$t('roster.comp.minimum'),
                productivityActualMax: this.$t('roster.main.max'),
                productivityPlanned: this.$t('roster.comp.monthPlan'),
                productivityDeviation: this.$t('shift.template.deviation'),
                feedback: this.$t('roster.comp.satisfaction'),
                feedbackUntil: this.$t('roster.comp.timeFrame'),
                feedbackCoverage: this.$t('roster.comp.participation'),
                actualWorkTime: this.$t('roster.comp.recordedWorkTime'),
                plannedWorkTime: this.$t('roster.comp.plannedWorkTime'),
                combinedIndicator: this.$t('roster.comp.indicator'),
                actualIndicator: this.$t('workBalance.summary.actual'),
                plannedIndicator: this.$t('workBalance.summary.target'),
                combinedMeo: this.$t('shift.main.meo'),
                actualMeo: this.$t('workBalance.summary.actual'),
                plannedMeo: this.$t('workBalance.summary.target'),
                combinedVlh: this.$t('shift.main.vlh'),
                actualVlh: this.$t('workBalance.summary.actual'),
                plannedVlh: this.$t('workBalance.summary.target'),
                assignedWorkTime: this.$t('roster.comp.assignedWorkTime'),
                availableWorkTime: this.$t('roster.comp.availableWorkTime'),
                prevMonthBalance: this.$t('workBalance.payroll.carryOver'),
                absenceHours: this.$t('shift.template.plannedAbsences'),
                marketEmployeeCounts: this.$t('roster.comp.marketComparison'),
            };

            let rowName = rowNames[row] || row;

            if (this.rowIsAverage(row)) rowName += ' (Ø)';

            return rowName;
        },

        getRowValue(row, past = false) {
            const stats = past ? this.pastStats : this.stats;
            if (row === 'marketEmployeeCounts') {
                const marketEmployeeCounts = stats.marketEmployeeCounts || [];
                return marketEmployeeCounts.length > 0 ? marketEmployeeCounts.join('–') : '--';
            }

            const statKeys = {
                // TODO: Determine whether to use the actual, planned or combined value
                accuracy: [ 'accuracy', 'combined' ],
                accuracyActual: [ 'accuracy', 'actual' ],
                accuracyPlanned: [ 'accuracy', 'planned' ],
                overstaffing: [ 'overstaffing', 'combined' ],
                overstaffingActual: [ 'overstaffing', 'actual' ],
                overstaffingPlanned: [ 'overstaffing', 'planned' ],
                understaffing: [ 'understaffing', 'combined' ],
                understaffingActual: [ 'understaffing', 'actual' ],
                understaffingPlanned: [ 'understaffing', 'planned' ],
                prediction: 'prediction',
                productivityActual: [ 'productivity', 'actual' ],
                productivityActualMin: [ 'productivity', 'actualMin' ],
                productivityActualMax: [ 'productivity', 'actualMax' ],
                productivityPlanned: [ 'productivity', 'planned' ],
                productivityDeviation: [ 'productivity', 'deviation' ],
                feedback: 'feedbackAvg',
                feedbackUntil: 'feedbackUntil',
                feedbackCoverage: 'feedbackCoverage',
                utilization: 'utilization',
                coverage: 'coverage',
                actualWorkTime: [ 'workTimeSums', 'actual' ],
                plannedWorkTime: [ 'workTimeSums', 'planned' ],
                combinedIndicator: 'combinedIndicatorSum',
                actualIndicator: 'actualIndicatorSum',
                plannedIndicator: 'plannedIndicatorSum',
                combinedMeo: 'combinedMeoValue',
                actualMeo: 'actualMeoValue',
                plannedMeo: 'plannedMeoValue',
                combinedVlh: 'combinedVlhValue',
                actualVlh: 'actualVlhValue',
                plannedVlh: 'plannedVlhValue',
                assignedWorkTime: 'assignedWorkTime',
                availableWorkTime: [ 'variableWorkTimeStats', 'workTimeHours' ],
                prevMonthBalance: [ 'variableWorkTimeStats', 'prevMonthBalance' ],
                absenceHours: [ 'variableWorkTimeStats', 'absenceHours' ],
                realWorkTimeHours: [ 'originalWorkTimeStats', 'workTimeHours' ],
                actualPreferenceHours: [ 'originalWorkTimeStats', 'preferenceHours' ],
            };

            const keys = statKeys[row];
            if (!stats) {
                return '--';
            } else if (row === 'feedbackUntil') {
                const fbUntil = moment(stats[keys]).format(DISPLAY_FORMATTERS.month);
                return stats.feedbackUntil ? fbUntil : '--';
            } else if (Array.isArray(keys)) {
                const s = stats[keys[0]];
                return s ? s[keys[1]] || '--' : '--';
            } else {
                return stats[keys];
            }
        },

        getRowPrefix(row) {
            const prefixes = {
                feedbackUntil: this.$t('global.general.to').toLowerCase() + ' ',
            };

            return prefixes[row] || null;
        },

        getRowPostfix(row) {
            const postfixes = {
                actualWorkTime: 'h',
                plannedWorkTime: 'h',
                // TODO: Check which unit to use for the indicator
                combinedIndicator: this.selectedIndicatorPostfix,
                actualIndicator: this.selectedIndicatorPostfix,
                plannedIndicator: this.selectedIndicatorPostfix,
                assignedWorkTime: 'h',
                availableWorkTime: 'h',
                prevMonthBalance: 'h',
                absenceHours: 'h',
                preferenceHours: 'h',
                marketEmployeeCounts: ' ' + this.$t('store.settings.shifts'),
                // overstaffing: this.showMonthValues ? 'h' : ' Sch.',
                // understaffing: this.showMonthValues ? 'h' : ' Sch.',
                productivityActual: this.selectedIndicatorPostfix,
                productivityActualMin: this.selectedIndicatorPostfix,
                productivityActualMax: this.selectedIndicatorPostfix,
                productivityPlanned: this.selectedIndicatorPostfix,
            };

            return postfixes[row] || null;
        },

        getRowHelperText(row) {
            const shiftsOrHr = this.showValuesInHours ? this.$tc('global.ui.hours', 2) : this.$t('store.settings.shifts');

            let indicatorUntil = '';
            if (this.stats.actualIndicatorUntil || this.stats.plannedIndicatorUntil) {
                indicatorUntil = this.stats.lineMode === 'actual'
                    ? this.stats.actualIndicatorUntil
                    : this.stats.plannedIndicatorUntil;

                indicatorUntil = indicatorUntil ? this.$t('global.general.to').toLowerCase() + ` ${indicatorUntil.format(DISPLAY_FORMATTERS.month)} - ` : '';
            }

            const actualShiftsUntil = this.stats.actualShiftsUntil ? this.$t('global.general.to') + ` ${this.stats.actualShiftsUntil.format('DD.MM.YYYY HH:mm')} - ` : '';
            const plannedShiftsUntil = this.stats.plannedShiftsUntil ? this.$t('global.general.to') + ` ${this.stats.plannedShiftsUntil.format(DISPLAY_FORMATTERS.month)} - ` : '';

            const rowHelperText = {
                accuracy: this.$t('roster.comp.accuracyHelp', [indicatorUntil, shiftsOrHr]),
                overstaffing: this.$t('roster.comp.overstaffedHelp', [indicatorUntil, shiftsOrHr]),
                understaffing: this.$t('roster.comp.understaffedHelp', [indicatorUntil, shiftsOrHr]),
                prediction: this.$t('roster.comp.forecastHelp'),
                utilization: this.$t('roster.comp.utilizationHelp', [indicatorUntil, shiftsOrHr]),
                coverage: this.$t('roster.comp.coverageHelp', [indicatorUntil, shiftsOrHr]),
                productivityText: this.$t('roster.comp.productivityTextHelp', [this.stats.indicatorTypeName ? `(${this.stats.indicatorTypeName}) ` : '']),
                feedbackText: this.$t('roster.comp.feedbackHelp'),
            };
            rowHelperText.accuracyActual = rowHelperText.accuracy;
            rowHelperText.accuracyPlanned = rowHelperText.accuracy;
            rowHelperText.overstaffingActual = rowHelperText.overstaffing;
            rowHelperText.overstaffingPlanned = rowHelperText.overstaffing;
            rowHelperText.understaffingActual = rowHelperText.understaffing;
            rowHelperText.understaffingPlanned = rowHelperText.understaffing;

            return rowHelperText[row] || null;
        },

        rowRoundTo(row) {
            const roundToMap = {
                feedback: 2,
                combinedIndicator: 2,
                actualIndicator: 2,
                plannedIndicator: 2,
                actualWorkTime: 2,
                plannedWorkTime: 2,
                availableWorkTime: 2,
                prevMonthBalance: 2,
                absenceHours: 2,
                realWorkTimeHours: 2,
                actualPreferenceHours: 2,
                combinedMeo: 2,
                actualMeo: 2,
                plannedMeo: 2,
                combinedVlh: 2,
                actualVlh: 2,
                plannedVlh: 2,
                productivityActual: 2,
                productivityActualMin: 2,
                productivityActualMax: 2,
                productivityPlanned: 2,
                overstaffing: this.showValuesInHours ? 2 : 0,
                overstaffingActual: this.showValuesInHours ? 2 : 0,
                overstaffingPlanned: this.showValuesInHours ? 2 : 0,
                understaffing: this.showValuesInHours ? 2 : 0,
                understaffingActual: this.showValuesInHours ? 2 : 0,
                understaffingPlanned: this.showValuesInHours ? 2 : 0,
            };
            return roundToMap[row] || 0;
        },

        rowIsAverage(row) {
            if (this.individualEntrySelected)
                return false;

            const avgList = [
                'accuracy',
                'prediction',
                'productivityActual',
                'productivityPlanned',
                'feedback',
                'feedbackCoverage',
            ];

            return avgList.includes(row) || false;
        },

        rowIsPercentage(row) {
            return [
                'accuracy',
                'accuracyActual',
                'accuracyPlanned',
                'prediction',
                'utilization',
                'coverage',
                'feedbackCoverage',
                'productivityDeviation',
            ].includes(row);
        },

        rowIsExpandable(row) {
            return [
                'combinedIndicator',
                'combinedMeo',
                'combinedVlh',
                'productivityActual',
            ].includes(row);
        },

        rowIsIndented(row) {
            const indentedRows = [
                'overstaffing',
                'overstaffingActual',
                'overstaffingPlanned',
                'understaffing',
                'understaffingActual',
                'understaffingPlanned',
                'actualIndicator',
                'plannedIndicator',
                'actualMeo',
                'plannedMeo',
                'actualVlh',
                'plannedVlh',
                'productivityActualMin',
                'productivityActualMax',
            ];
            return indentedRows.includes(row);
        },

        openSumPopup() {
            // Popup: Sollstunden insges. lt. Verträge, Abwesenheiten, Wert vom Vormonat, Indikatorwerte (monatssumme, soll/ist)
            const mode = this.stats.mode;

            const modeLabelMap = {
                'month': this.$tc('global.general.month'),
                // 'monthHourly': 'Monat',
                'week': this.$t('roster.comp.week'),
                'day': this.$tc('global.general.day'),
            };

            const sumLabelMap = {
                'month': this.$t('roster.comp.monthlySums'),
                // 'monthHourly': 'Monatssummen',
                'week': this.$t('roster.comp.weeklySums'),
                'day': this.$t('roster.comp.dailySums'),
            };
            const sumLabel = sumLabelMap[mode] || this.$t('roster.comp.sums');

            let originalWorkTimeStats = { workTimeHours: null, preferenceHours: null, prevMonthBalance: null };
            if (this.stats && this.stats.originalWorkTimeStats) {
                originalWorkTimeStats = this.stats.originalWorkTimeStats;
            }
            let variableWorkTimeStats = { workTimeHours: null, absenceHours: null, prevMonthBalance: null };
            if (this.stats && this.stats.variableWorkTimeStats) {
                variableWorkTimeStats = this.stats.variableWorkTimeStats;
            }

            let multiplyWTFactor = 1;
            if (mode !== 'month' && this.stats.additionalData) {
                const multiplyWTFactorMap = {
                    'actual': this.stats.additionalData.actualWTPercent,
                    'planned': this.stats.additionalData.plannedWTPercent,
                    'combined': this.stats.additionalData.wtPercent,
                };
                multiplyWTFactor = multiplyWTFactorMap[this.stats.lineMode];
                if (!multiplyWTFactor) multiplyWTFactor = null;
            }

            // console.log(this.stats);

            const formatText = (value, digits = 2) => {
                if (value === null || value === undefined)
                    return '--';
                return ` ${value.toFixed(digits)}`;
            };

            const workTimeHours = formatText(originalWorkTimeStats.workTimeHours);
            const absenceHours = formatText(originalWorkTimeStats.preferenceHours);
            const prevMonthBalance = formatText(originalWorkTimeStats.prevMonthBalance);
            const realWorkTimeHours = formatText(variableWorkTimeStats.workTimeHours * multiplyWTFactor || null);
            const plannedIndicatorSum = formatText(this.stats ? this.stats.plannedIndicatorSum : null);
            const actualIndicatorSum = formatText(this.stats ? this.stats.actualIndicatorSum : null);

            const indicatorTypeName = this.stats ? `${this.stats.indicatorTypeName}` : '';

            this.$helpers.OpenPopup({
                type: 'datafill',
                header: this.$t('roster.comp.personnelUsageSum', [sumLabel]),
                expanded: true,
                centered: true,
                componentState: {
                    entries: [
                        {
                            label: this.$t('roster.comp.totalContractTarget') + ( mode !== 'month' ? this.$t('roster.comp.monthBubble') : '' ),
                            type: 'output',
                            value: workTimeHours,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            label: this.$tc('global.general.absence', 2),
                            type: 'output',
                            value: absenceHours,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            label: this.$t('roster.comp.previousMonthValues'),
                            type: 'output',
                            value: prevMonthBalance,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            label: this.$t('roster.comp.availableTime') + (mode !== 'month' ? ` (${modeLabelMap[mode]})` : ''),
                            type: 'output',
                            value: realWorkTimeHours,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            divider: true,
                        },
                        {
                            label: this.$t('roster.comp.indicator'),
                            type: 'output',
                            value: indicatorTypeName,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            label: this.$t('roster.comp.targetSum'),
                            type: 'output',
                            value: plannedIndicatorSum,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                        {
                            label: this.$t('roster.comp.actualSum'),
                            type: 'output',
                            value: actualIndicatorSum,
                            labelStyles: { alignItems: 'baseline' },
                            disabled: true,
                        },
                    ],
                },
            });
        },

        openInfoVideoPopup() {
            this.$emit('openInfoVideo');
            // this.infoVideoOpened = true;
        },
    },
}
</script>

<style scoped>

.pdd-container {
    display: flex;
    flex-direction: column;
    color: var(--contrast-4);
    background-color: var(--contrast-0);
}

.pdd-title {
    display: flex;
    align-items: baseline;
    height: 40px;
    padding: 0.5rem;
    background-color: var(--contrast-1);
    color: var(--contrast-4);
    border-bottom: 5px solid var(--contrast-1);
}

.pdd-title-selected {
    box-shadow: 0 5px 0 0 #6d8d23;
    z-index: 1;
}

.pdd-title > h5 {
    flex: 1;
    margin: 0;
}

.pdd-title .light {
    font-family: DistrictProLight, sans-serif;
}

.pdd-title .bold {
    font-family: DistrictProBold, sans-serif;
}

.pdd-subtitle {
    font-size: 0.7rem;
    margin-left: 12px;
    color: var(--contrast-4);
    opacity: 1.0;
}

.pdd-body {
    display: flex;
    flex-direction: column;
    padding: 0.25rem 0.5rem;
    overflow-y: auto;
}

.pdd-body > hr {
    border-color: var(--contrast-3);
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
}

.pdd-body .section-header {
    font: 1rem DistrictProDemiBold, sans-serif;
    text-align: left;
    margin: 0.5rem 0 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.pdd-body > .pdd-row {
    color: var(--contrast-4);
    display: flex;
    text-align: left;
    gap: 1rem;
    font-size: 1rem;
    margin: 0.125rem 0;
}

.pdd-body > .pdd-row > div {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
}

.pdd-body > .pdd-row > div > button {
    margin: 0;
    padding: 0;
    border: none;
    outline: none;
    background-color: transparent;
    color: var(--contrast-4) !important;
}

.pdd-body > .pdd-row .hover-text-container {
    max-width: 400px;
}

.pdd-body > .pdd-row > :first-child {
    flex: 1;
}

.pdd-row.bold, .pdd-row .bold {
    font-weight: bold;
}

.pdd-body .red {
    color: #ee134d;
}

.pdd-body .orange {
    color: #df6a19;
}

.pdd-body .green {
    color: #6d8d23;
}

.pdd-body .blue {
    color: #2f61ab;
}

.pdd-header-row {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    text-align: right;
    background-color: var(--contrast-1);
}

.pdd-header-row > div {
    flex: 2;
    text-align: center;
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
}

.pdd-header-row > div:first-child {
    flex: 4;
    text-align: left;
}

.pdd-header-row > div:last-child {
    flex: 0;
}

.pdd-sidebar-container {
    display: flex;
    flex-direction: row;
}

.pdd-tab-group {
    flex: 1;
}

.pdd-tab-group--indent {
    flex: 0;
    flex-basis: calc(100% - 2rem);
}

.pdd-sidebar {
    flex: 0;
    position: absolute;
    top: 52px;
    right: 0;
    bottom: 1.75rem;
    padding-top: 0.1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    background-color: var(--contrast-0);
}

.pdd-sidebar-icon {
    display: flex;
    width: 2rem;
    height: 2rem;
    font-size: 1.25rem;
    justify-content: center;
    align-items: center;
}

.info-button {
    margin-left: auto;
}

.stats-button {
    background-color: transparent;
    border: none;
    outline: none;
    color: var(--contrast-5);
    font-family: "District Pro", sans-serif;
    font-size: 0.85rem;
    transition: 125ms ease-in-out color, 125ms ease-in-out background-color;
    cursor: pointer;
    padding: 0.125rem 0.25rem;
    margin-bottom: -0.125rem;
    margin-top: -0.125rem;
}

.stats-button:hover {
    background-color: var(--contrast-2);
    color: var(--contrast-4);
}


.hover-text-container {
    max-width: 440px;
    font-weight: normal;
    text-align: left;
}

.info-video-button {
    position: absolute;
    right: 0.5rem;
    height: 1.5rem;
    width: 2rem;
    margin-left: auto;
    background: transparent;
    color: var(--contrast-5);
    outline: none;
    border: none;
    box-shadow: none;
}
</style>

<style>
.tab-group__tab-row__tab:not(.tab-group__tab-row__tab--selected) {
    background-color: var(--contrast-0) !important;
    transition: none !important;
}

.tab-group__tab-row > .tab-group__tab-row__tab:last-child {
    flex: 1;
}

.tab-group__tab-row__tab:hover {
    /*background-color: var(--contrast-2) !important;*/
    background-color: var(--contrast-0) !important;
    cursor: default !important;
}

.tab-group__tab-row__tab--selected {
    box-shadow: inset 0 2px 0 0 var(--contrast-5) !important;
    background-color: var(--contrast-0) !important;
}

.tab-group__tab-row {
    background-color: var(--contrast-1) !important;
}

.tab-group__tab-body {
    height: 268px;
    padding-bottom: 0.25rem;
}

.tab-group__tab-row__tab--selected {
    display: none !important;
}
</style>
